import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { email } from '@vuelidate/validators';

export const MODE = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export const INPUT_TYPES = {
  EMAIL: 'email',
  TEL: 'tel',
  TEXT: 'text',
};

export const looksLikePhoneNumber = value => {
  const trimmedValue = typeof value === 'string' ? value.trim() : '';
  return /^[+\d(][\d\s().-]*$/.test(trimmedValue);
};

export const validatePhoneNumber = (value, defaultCountry) => {
  if (!value) return true;
  try {
    return isValidPhoneNumber(value, defaultCountry || undefined);
  } catch (error) {
    return false;
  }
};

export const formatPhoneNumber = (value, defaultCountry) => {
  try {
    const phoneNumber = parsePhoneNumber(value, defaultCountry || undefined);
    return {
      isValid: phoneNumber?.isValid() || false,
      formattedValue: phoneNumber?.formatInternational() || value,
      normalizedValue: phoneNumber?.format('E.164') || value,
    };
  } catch (error) {
    return { isValid: false, formattedValue: value };
  }
};

export const getValidationRules = (type, defaultCountry) => ({
  newTag: {
    ...(type === INPUT_TYPES.EMAIL ? { email } : {}),
    ...(type === INPUT_TYPES.TEL
      ? {
          isValidPhone: value => validatePhoneNumber(value, defaultCountry),
        }
      : {}),
  },
});

export const checkTagTypeValidity = (type, value, v$, defaultCountry) => {
  if (type === INPUT_TYPES.TEL) {
    return !validatePhoneNumber(value, defaultCountry);
  }
  return v$.$invalid;
};

export const validateAndFormatNewTag = (
  trimmedNewTag,
  type,
  isNewTagInValidType,
  defaultCountry
) => {
  let isValid = true;
  let formattedValue = trimmedNewTag;

  if (type === INPUT_TYPES.EMAIL) {
    isValid = !isNewTagInValidType;
  } else if (type === INPUT_TYPES.TEL) {
    const {
      isValid: phoneValid,
      formattedValue: phoneFormatted,
      normalizedValue,
    } = formatPhoneNumber(trimmedNewTag, defaultCountry);
    isValid = phoneValid;
    formattedValue = phoneFormatted;
    return { isValid, formattedValue, normalizedValue };
  }

  return { isValid, formattedValue };
};

export const createNewTagMenuItem = (
  formattedValue,
  trimmedNewTag,
  type,
  normalizedValue = trimmedNewTag
) => ({
  label: formattedValue,
  value: normalizedValue,
  ...(type === INPUT_TYPES.EMAIL ? { email: trimmedNewTag } : {}),
  ...(type === INPUT_TYPES.TEL ? { phoneNumber: normalizedValue } : {}),
  thumbnail: { name: formattedValue, src: '' },
  action: 'create',
});

export const buildTagMenuItems = ({
  mode,
  tags,
  menuItems,
  newTag,
  isLoading,
  type,
  isNewTagInValidType,
  allowCreate = true,
  skipLabelDedup = false,
  defaultCountry,
}) => {
  if (mode === MODE.SINGLE && tags.length >= 1) return [];

  const availableMenuItems = skipLabelDedup
    ? menuItems
    : menuItems.filter(item => !tags.includes(item.label));

  // Show typed value as suggestion only if:
  // 1. There's a value being typed
  // 2. The value isn't already in the tags
  // 3. Validation passes (email/phone) and There are no menu items available
  // 4. allowCreate is enabled
  const trimmedNewTag = newTag?.trim();
  const shouldShowCreateSuggestion =
    allowCreate &&
    trimmedNewTag &&
    !tags.includes(trimmedNewTag) &&
    !isLoading &&
    !availableMenuItems.length;

  if (shouldShowCreateSuggestion) {
    const { isValid, formattedValue, normalizedValue } =
      validateAndFormatNewTag(
        trimmedNewTag,
        type,
        isNewTagInValidType,
        defaultCountry
      );

    if (isValid) {
      return [
        createNewTagMenuItem(
          formattedValue,
          trimmedNewTag,
          type,
          normalizedValue
        ),
      ];
    }
  }

  return availableMenuItems;
};

export const canAddTag = (mode, tagsLength) =>
  !(mode === MODE.SINGLE && tagsLength >= 1);

export const findMatchingMenuItem = (menuItems, value) =>
  menuItems.find(item => item.email === value);
